import { Button, Dialog, Input, Field } from "@/components/ui";
import React, { useRef, useState, useContext, useEffect } from "react";
import { PlayerAccount } from "@/interfaces";
import { PlayersServices } from "@/services";
import { toast } from "sonner";
import ReactCrop, { Crop, PixelCrop, centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { EnvContext } from "@/contexts/appContexts";
import { saveImageToFirebase } from "@/utils/firebase";
import { Svg } from "@/assets";
import { z } from "zod";
import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";

interface Props {
  playerAccount: PlayerAccount;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clearCaches: () => void;
  reloadPlayer: () => void;
}

const profileSchema = z.object({
  firstName: z.string().min(1, "First name is required").max(255, "First name must be less than 255 characters"),
  lastName: z.string().min(1, "Last name is required").max(255, "Last name must be less than 255 characters"),
  email: z
    .email("Invalid email address")
    .min(1, "Email is required")
    .max(255, "Email must be less than 255 characters"),
  internalIdentifier: z.string().max(255, "Internal identifier must be less than 255 characters").optional(),
  photoUrl: z.string().optional(),
  phone: z.string().max(255, "Phone must be less than 255 characters").optional(),
  title: z.string().max(255, "Title must be less than 255 characters").optional(),
});

type ProfileFormValues = z.infer<typeof profileSchema>;

export default function EditProfileDialog({ open, onOpenChange, playerAccount, clearCaches, reloadPlayer }: Props) {
  const [loading, setLoading] = useState<boolean>(false);
  const [imgSrc, setImgSrc] = useState("");
  const [imgRef, setImgRef] = useState<HTMLImageElement | null>(null);
  const [crop, setCrop] = useState<Crop>();
  const [completedCrop, setCompletedCrop] = useState<PixelCrop>();
  const [isCropping, setIsCropping] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const envValues = useContext(EnvContext);

  const form = useForm<ProfileFormValues>({
    resolver: zodResolver(profileSchema),
    mode: "onTouched",
    defaultValues: {
      firstName: playerAccount?.firstName || "",
      lastName: playerAccount?.lastName || "",
      email: playerAccount?.email || "",
      internalIdentifier: playerAccount?.internalIdentifier || "",
      photoUrl: playerAccount?.photoUrl || "",
      phone: playerAccount?.phone || "",
      title: playerAccount?.title || "",
    },
  });

  // Reset form when playerAccount or open state changes
  useEffect(() => {
    if (open && playerAccount) {
      form.reset({
        firstName: playerAccount.firstName || "",
        lastName: playerAccount.lastName || "",
        email: playerAccount.email || "",
        internalIdentifier: playerAccount.internalIdentifier || "",
        photoUrl: playerAccount.photoUrl || "",
        phone: playerAccount.phone || "",
        title: playerAccount.title || "",
      });
    }
  }, [open, playerAccount, form]);

  const onSelectFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      const reader = new FileReader();
      reader.addEventListener("load", () => setImgSrc(reader.result?.toString() || ""));
      reader.readAsDataURL(e.target.files[0]);
      setIsCropping(true);
    }
  };

  const onImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
    const { width, height } = e.currentTarget;
    setImgRef(e.currentTarget);

    const initialCrop = centerCrop(
      makeAspectCrop(
        {
          unit: "%",
          width: 80,
        },
        1,
        width,
        height
      ),
      width,
      height
    );

    setCrop(initialCrop);
  };

  const getCroppedImg = (image: HTMLImageElement, crop: PixelCrop): Promise<Blob> => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;
    const ctx = canvas.getContext("2d");

    if (ctx) {
      ctx.imageSmoothingEnabled = true;
      ctx.imageSmoothingQuality = "high";

      ctx.drawImage(
        image,
        crop.x * scaleX,
        crop.y * scaleY,
        crop.width * scaleX,
        crop.height * scaleY,
        0,
        0,
        canvas.width,
        canvas.height
      );
    }

    return new Promise((resolve, reject) => {
      canvas.toBlob(
        blob => {
          if (!blob) {
            reject(new Error("Canvas is empty"));
            return;
          }
          resolve(blob);
        },
        "image/jpeg",
        0.95
      );
    });
  };

  const handleSaveCrop = async () => {
    if (imgRef && completedCrop) {
      setLoading(true);
      try {
        const blob = await getCroppedImg(imgRef, completedCrop);
        const fileName = `profile_${Date.now()}`;
        const uploadedUrl = await saveImageToFirebase(envValues, playerAccount.userId, blob, fileName);
        form.setValue("photoUrl", uploadedUrl, { shouldDirty: true });
        setIsCropping(false);
        toast.success("Photo uploaded successfully");
      } catch (e) {
        console.error(e);
        toast.error("Failed to upload photo");
      } finally {
        setLoading(false);
      }
    }
  };

  const onSubmit = (data: ProfileFormValues) => {
    if (!playerAccount) return;

    setLoading(true);
    PlayersServices.updatePlayerProfile(playerAccount.playerId!, data)
      .then(() => {
        clearCaches();
        reloadPlayer();
        onOpenChange(false);
      })
      .catch((e: string) => {
        const errorMessage = e.substring(0, 40);
        if (errorMessage.includes("Username already taken")) {
          toast.error("This email address is already in use. Please choose a different one.");
        } else {
          toast.error(errorMessage);
        }
      })
      .finally(() => setLoading(false));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange} size="lg">
      <Dialog.Header>
        <Dialog.Title>{isCropping ? "Crop Photo" : "Edit Profile"}</Dialog.Title>
      </Dialog.Header>
      <Dialog.Content>
        {isCropping ? (
          <div className="flex flex-col items-center justify-center py-4 overflow-hidden">
            <ReactCrop
              crop={crop}
              onChange={c => setCrop(c)}
              onComplete={c => setCompletedCrop(c)}
              aspect={1}
              className="max-w-full"
            >
              <img
                src={imgSrc}
                onLoad={onImageLoad}
                alt="Crop me"
                className="max-w-full max-h-[50vh] block object-contain"
              />
            </ReactCrop>
          </div>
        ) : (
          <form id="edit-profile-form" onSubmit={form.handleSubmit(onSubmit)}>
            <Field.Group>
              <Field>
                <Field.Label>Profile Photo</Field.Label>
                <div className="flex items-center gap-4">
                  <img
                    src={form.watch("photoUrl") || Svg.noImageuser}
                    alt="Profile"
                    className="w-20 h-20 rounded-full object-cover border"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => fileInputRef.current?.click()}
                    disabled={loading}
                  >
                    Change Photo
                  </Button>
                  <input type="file" ref={fileInputRef} onChange={onSelectFile} accept="image/*" className="hidden" />
                </div>
              </Field>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Controller
                  name="firstName"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Field.Label>First Name</Field.Label>
                      <Input {...field} disabled={loading} placeholder="First Name" />
                      {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <Controller
                  name="lastName"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Field.Label>Last Name</Field.Label>
                      <Input {...field} disabled={loading} placeholder="Last Name" />
                      {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <Controller
                  name="email"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Field.Label>Email</Field.Label>
                      <Input {...field} disabled={loading} placeholder="Email" type="email" />
                      {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <Controller
                  name="phone"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Field.Label>Phone</Field.Label>
                      <Input {...field} disabled={loading} placeholder="Phone" />
                      {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <Controller
                  name="title"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Field.Label>Title</Field.Label>
                      <Input {...field} disabled={loading} placeholder="Title" />
                      {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
                <Controller
                  name="internalIdentifier"
                  control={form.control}
                  render={({ field, fieldState }) => (
                    <Field data-invalid={fieldState.invalid}>
                      <Field.Label>Internal Identifier</Field.Label>
                      <Input {...field} disabled={loading} placeholder="For your own reporting..." />
                      {fieldState.invalid && <Field.Error errors={[fieldState.error]} />}
                    </Field>
                  )}
                />
              </div>
            </Field.Group>
          </form>
        )}
      </Dialog.Content>
      <Dialog.Footer>
        {isCropping ? (
          <>
            <Button variant="ghost" onClick={() => setIsCropping(false)} disabled={loading}>
              Cancel
            </Button>
            <Button onClick={handleSaveCrop} disabled={loading || !completedCrop}>
              {loading ? "Processing..." : "Crop & Use"}
            </Button>
          </>
        ) : (
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={loading}>
              Cancel
            </Button>
            <Button type="submit" form="edit-profile-form" disabled={loading} loading={loading}>
              Save Changes
            </Button>
          </>
        )}
      </Dialog.Footer>
    </Dialog>
  );
}
